import { useCallback } from 'react'

import { useQueryClient, useQuery } from '@tanstack/react-query'
import { chunk, debounce, partition } from 'lodash'
import { formatUnits } from 'viem'

import { SupportedChainId } from '~/constants/chains'
import { QUERY_KEYS } from '~/constants/react-query'
import { TOKEN_PRICE_DECIMALS } from '~/constants/tokens'
import { useGetTokenMetadata } from '~/hooks/useTokenMetadata'
import { fetchPrices } from '~/hooks/useTokenPrices/fetchPrices'
import { fetchRelayPrice } from '~/hooks/useTokenPrices/fetchRelayPrice'
import { IAddress } from '~/types'
import { isListedChainId } from '~/utils'
import { Address } from '~/utils/address'
import { getAmountFromUSDValue, multiplyWithPrecision } from '~/utils/numbers'

import type { QueryClient } from '@tanstack/react-query'

const PRICE_CACHE_TTL = 60_000 * 1 // 1 minute

export type PendingPricingCall = {
  chainId: SupportedChainId
  address: string
}

const getPendingPricingCallsQueryKey = () => [QUERY_KEYS.PENDING_PRICING_CALLS]
export const getTokenPriceQueryKey = (
  chainId: SupportedChainId | number,
  address: string,
) => [QUERY_KEYS.TOKEN_PRICE, chainId, Address.format(address)]
const getPricesVersionQueryKey = () => [QUERY_KEYS.PRICES_VERSION]

const incrementPricesVersion = (queryClient: QueryClient) => {
  const versionQueryKey = getPricesVersionQueryKey()
  const currentVersion = queryClient.getQueryData<number>(versionQueryKey) || 0
  queryClient.setQueryData(versionQueryKey, currentVersion + 1)
}

const PRICES_REQUEST_LENGTH = 100
const PRICING_CALLS_RESOLUTION_DEBOUNCE_IN_MS = 250

// Module-level (global) debounced function to ensure it's shared across all hook instances
let globalDebouncedResolve: ReturnType<typeof debounce> | null = null

// Export for testing purposes
export const __testing__ = {
  resetGlobalDebouncedResolve: () => {
    if (globalDebouncedResolve) {
      globalDebouncedResolve.cancel()
      globalDebouncedResolve = null
    }
  },
}

// Custom hook to get the prices version from cache
// This is useful to make some hooks reactive to price updates
const usePricesVersion = () => {
  const queryClient = useQueryClient()

  const { data: pricesVersion } = useQuery({
    queryKey: getPricesVersionQueryKey(),
    queryFn: () => {
      return queryClient.getQueryData<number>(getPricesVersionQueryKey()) || 0
    },
    // Disabling because this useQuery only exists to
    // listen to any changes in the prices version key
    enabled: false,
  })

  return pricesVersion
}

export const useTokenPrices = () => {
  const queryClient = useQueryClient()
  const getTokenMetadata = useGetTokenMetadata()

  // Get the current prices version for reactivity - using the hook to make it reactive
  const pricesVersion = usePricesVersion()

  // Remove pending pricing call from cache
  const removePendingPricingCall = useCallback(
    (chainId: SupportedChainId, address: string) => {
      const queryKey = getPendingPricingCallsQueryKey()
      const currentCalls =
        queryClient.getQueryData<PendingPricingCall[]>(queryKey) || []

      const filteredCalls = currentCalls.filter(
        (call) =>
          !(call.chainId === chainId && Address.equal(call.address, address)),
      )

      queryClient.setQueryData(queryKey, filteredCalls)
    },
    [queryClient],
  )

  const isPriceCached = useCallback(
    (chainId: SupportedChainId, address: string) => {
      const priceQueryKey = getTokenPriceQueryKey(chainId, address)
      const cachedPrice = queryClient.getQueryState(priceQueryKey)
      return (
        cachedPrice?.dataUpdatedAt &&
        cachedPrice.data !== undefined &&
        cachedPrice.dataUpdatedAt >= Date.now() - PRICE_CACHE_TTL
      )
    },
    [queryClient],
  )

  // Convert token amount to USD value
  const convertTokenToUSD = useCallback(
    (options: {
      token: string
      amount: bigint
      chainId: SupportedChainId
    }): number | undefined => {
      const { token, amount, chainId } = options

      // Get price from cache
      const priceQueryKey = getTokenPriceQueryKey(chainId, token)
      const cachedPrice = queryClient.getQueryData<number>(priceQueryKey)

      // Return undefined if price not cached yet (still loading)
      if (cachedPrice === undefined) return undefined

      // Return 0 if price is actually 0 (no price available)
      if (cachedPrice === 0) return 0

      const tokenMetadata = getTokenMetadata({
        chainId,
        address: token as IAddress,
      })

      // Return undefined if metadata not available yet
      if (!tokenMetadata) return undefined

      const decimals = tokenMetadata.decimals

      return parseFloat(
        multiplyWithPrecision(
          {
            amount: formatUnits(amount, decimals),
            decimal: decimals,
          },
          {
            amount: cachedPrice.toString(),
            decimal: TOKEN_PRICE_DECIMALS,
          },
        ),
      )
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [queryClient, getTokenMetadata, pricesVersion],
  )

  // Convert USD to token amount using cached prices
  const convertUSDToToken = useCallback(
    (
      currencyValue: number,
      token: string,
      chainId: SupportedChainId,
    ): string => {
      // Get price from cache
      const priceQueryKey = getTokenPriceQueryKey(chainId, token)
      const cachedPrice = queryClient.getQueryData<number>(priceQueryKey)

      if (!cachedPrice || cachedPrice === 0) return '0'

      const tokenMetadata = getTokenMetadata({
        chainId,
        address: token as IAddress,
      })

      if (!tokenMetadata) return '0'

      const decimals = tokenMetadata.decimals

      return getAmountFromUSDValue({
        tokenUSDValue: currencyValue.toString(),
        tokenDecimals: decimals,
        tokenPrice: cachedPrice.toString(),
      })
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [queryClient, getTokenMetadata, pricesVersion],
  )

  // Internal function to actually resolve pricing calls
  const _resolvePricingCalls = useCallback(async () => {
    const queryKey = getPendingPricingCallsQueryKey()
    const pendingCalls =
      queryClient.getQueryData<PendingPricingCall[]>(queryKey) || []

    if (pendingCalls.length === 0) return

    // Filter out calls that already have cached prices
    // This effectively keep prices stale until the next page reload
    const uncachedCalls = pendingCalls.filter(
      (call) => !isPriceCached(call.chainId, call.address),
    )

    if (uncachedCalls.length === 0) {
      // All calls are already cached, just clear pending calls
      queryClient.setQueryData(queryKey, [])
      return
    }

    try {
      // Split calls into relay tokens (unsupported chains) and regular tokens
      const [relayCalls, regularCalls] = partition(
        uncachedCalls,
        (call) => !isListedChainId(call.chainId),
      )

      // Fetch prices from both sources in parallel
      await Promise.all([
        // Fetch relay prices individually
        ...relayCalls.map(async (call) => {
          const price = await fetchRelayPrice(
            call.address as IAddress,
            call.chainId,
          )
          if (price !== undefined) {
            const priceQueryKey = getTokenPriceQueryKey(
              call.chainId,
              call.address,
            )
            queryClient.setQueryData(priceQueryKey, price)
          }
        }),
        // Fetch regular prices in batches
        ...chunk(regularCalls, PRICES_REQUEST_LENGTH).map(
          async (groupedPendingCalls) => {
            const results = await fetchPrices(groupedPendingCalls)

            // Store prices in cache
            results.forEach(({ address, chainId, price }) => {
              const priceQueryKey = getTokenPriceQueryKey(chainId, address)
              queryClient.setQueryData(priceQueryKey, price)
            })
          },
        ),
      ])

      // Update the prices version in cache when we finish fetching new prices
      incrementPricesVersion(queryClient)
    } finally {
      // Clear pending calls regardless of success or failure
      queryClient.setQueryData(queryKey, [])
    }
  }, [queryClient, isPriceCached])

  const resolvePricingCalls = useCallback(() => {
    if (!globalDebouncedResolve) {
      globalDebouncedResolve = debounce(
        _resolvePricingCalls,
        PRICING_CALLS_RESOLUTION_DEBOUNCE_IN_MS,
      )
    }
    globalDebouncedResolve()
  }, [_resolvePricingCalls])

  // Add multiple pending pricing calls to cache
  const addMultiplePendingPricingCalls = useCallback(
    (calls: Array<{ chainId: SupportedChainId; address: string }>) => {
      const queryKey = getPendingPricingCallsQueryKey()
      const currentCalls =
        queryClient.getQueryData<PendingPricingCall[]>(queryKey) || []

      // Filter out calls that already exist in pending calls or are already cached
      const newCalls = calls.filter((call) => {
        // Check if already in pending calls
        const alreadyPending = currentCalls.some(
          (existingCall) =>
            existingCall.chainId === call.chainId &&
            Address.equal(existingCall.address, call.address),
        )

        if (alreadyPending) return false

        // Check if already cached in token price
        return !isPriceCached(call.chainId, call.address)
      })

      if (newCalls.length > 0) {
        const pendingCallsToAdd: PendingPricingCall[] = newCalls.map(
          (call) => ({
            chainId: call.chainId,
            address: Address.format(call.address),
          }),
        )

        queryClient.setQueryData(queryKey, [
          ...currentCalls,
          ...pendingCallsToAdd,
        ])
      }
      resolvePricingCalls()
    },
    [queryClient, resolvePricingCalls, isPriceCached],
  )

  const getPrices = useCallback(
    (tokens: { chainId: SupportedChainId; address: string }[]) => {
      // Add pending pricing calls so we'll get the price eventually
      addMultiplePendingPricingCalls(tokens)

      return tokens.map(({ chainId, address }) => {
        const priceQueryKey = getTokenPriceQueryKey(chainId, address)
        return queryClient.getQueryData<number>(priceQueryKey)
      })
    },
    [queryClient, addMultiplePendingPricingCalls],
  )

  const hasPendingPricingCalls = useCallback(() => {
    const queryKey = getPendingPricingCallsQueryKey()
    const pendingCalls =
      queryClient.getQueryData<PendingPricingCall[]>(queryKey) || []
    return pendingCalls.length > 0
  }, [queryClient])

  // Add pending pricing call to cache
  const addPendingPricingCall = useCallback(
    (chainId: SupportedChainId, address: string) => {
      addMultiplePendingPricingCalls([{ chainId, address }])
    },
    [addMultiplePendingPricingCalls],
  )

  const getPrice = useCallback(
    (chainId: SupportedChainId, address: string) => {
      // Add pending pricing call so we'll get the price eventually
      addPendingPricingCall(chainId, address)

      const priceQueryKey = getTokenPriceQueryKey(chainId, address)
      return queryClient.getQueryData<number>(priceQueryKey)
    },
    [queryClient, addPendingPricingCall],
  )

  return {
    addPendingPricingCall,
    addMultiplePendingPricingCalls,
    removePendingPricingCall,
    convertTokenToUSD,
    convertUSDToToken,
    resolvePricingCalls,
    getPrice,
    getPrices,
    hasPendingPricingCalls,
    usePricesVersion,
  }
}
